/**
 * Extracted answers, one row per configured question (prompt → answer), then
 * any extra keys the call produced that the config does not know. A missing
 * answer shows "—"; a missing REQUIRED answer is called out, because that is
 * what sends a candidate to "Needs review" — but only once a call has
 * produced answers (`flagMissing`); before the first completed call the
 * table is just the list of what will be asked (QA stage 4).
 */
import { answerLabel } from '@/features/screening/screening-format';
import { isMissing } from '@/features/screening/qualify';
import type { ScreeningQuestion } from '@/features/screening/types';

export function ScreeningAnswers({
  questions,
  answers,
  flagMissing = true,
}: {
  questions: ScreeningQuestion[];
  answers: Record<string, unknown>;
  /** Call out missing required answers (false while no call has produced any answers). */
  flagMissing?: boolean;
}) {
  const known = new Set(questions.map((q) => q.key));
  const extras = Object.entries(answers).filter(([k]) => !known.has(k));
  if (questions.length === 0 && extras.length === 0) {
    return <p className="text-[13px] text-muted">No answers yet. They appear here after the first completed call.</p>;
  }
  return (
    <table className="w-full border-collapse text-[13px]" data-testid="screening-answers">
      <caption className="sr-only">Answers captured on the screening call</caption>
      <thead>
        <tr className="text-left text-[12px] text-muted">
          <th scope="col" className="pb-1.5 pr-3 font-medium">Question</th>
          <th scope="col" className="pb-1.5 font-medium">Answer</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {questions.map((q) => {
          const v = answers[q.key];
          const missing = isMissing(v);
          return (
            <tr key={q.key} className="align-top">
              <th scope="row" className="py-2 pr-3 font-normal text-gray-700">
                {q.prompt || q.key.replace(/_/g, ' ')}
              </th>
              <td className="py-2 text-gray-900">
                {missing && q.required && flagMissing ? (
                  <span className="font-medium text-warning-text" data-testid={`missing-${q.key}`}>
                    Not answered <span className="font-normal">(required)</span>
                  </span>
                ) : (
                  answerLabel(v)
                )}
              </td>
            </tr>
          );
        })}
        {extras.map(([k, v]) => (
          <tr key={k} className="align-top">
            <th scope="row" className="py-2 pr-3 font-normal text-gray-700">{k.replace(/_/g, ' ')}</th>
            <td className="py-2 text-gray-900">{answerLabel(v)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
